import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  MAX_CHAT_FILE_BYTES,
  MAX_CHAT_FILE_ATTACHMENTS,
  MAX_TOTAL_CHAT_ATTACHMENTS,
  toPendingChatFileAttachment,
  type PendingChatFileAttachment,
} from './chatFileAttachment';

type UseChatFileAttachments = {
  attachments: PendingChatFileAttachment[];
  // ファイルを添付に追加する（上限・サイズ超過はトーストで通知。形式の可否はバックエンドが判定する）
  addFiles: (files: File[]) => void;
  removeAttachment: (id: string) => void;
  clear: () => void;
};

/**
 * チャット入力欄のファイル（スプレッドシート・テキストファイル）添付状態を管理するフック。
 * 画像と違い圧縮・ObjectURL は不要なので状態管理のみ。追加時に個数・サイズを検証する
 * （形式の可否は拡張子で絞り込まず、バックエンドが内容そのもので判定する）。
 *
 * @param getReservedSlots 画像など他種の添付が占有している枠数を返す関数（合計上限の算出に使う）。
 *   関数で受け取ることで、画像フックとの相互参照（TDZ）を避けつつ最新の枚数を参照する。
 */
export function useChatFileAttachments(getReservedSlots: () => number = () => 0): UseChatFileAttachments {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState<PendingChatFileAttachment[]>([]);

  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      // 以下の検証はすべて all-or-nothing。1件でも弾かれるものがあれば全件中止する。
      const fileRemaining = MAX_CHAT_FILE_ATTACHMENTS - attachments.length;
      const totalRemaining = MAX_TOTAL_CHAT_ATTACHMENTS - getReservedSlots() - attachments.length;

      if (files.length > fileRemaining) {
        toast.error(t('viewModel.aiChat.fileCountLimit', { max: MAX_CHAT_FILE_ATTACHMENTS }));
        return;
      }
      if (files.length > totalRemaining) {
        toast.error(t('viewModel.aiChat.attachmentTotalLimit', { max: MAX_TOTAL_CHAT_ATTACHMENTS }));
        return;
      }
      if (files.some((file) => file.size >= MAX_CHAT_FILE_BYTES)) {
        toast.error(t('viewModel.aiChat.fileSizeLimit', { max: MAX_CHAT_FILE_BYTES / (1024 * 1024) }));
        return;
      }

      setAttachments((prev) => {
        // 追加中に他経路（画像の圧縮完了など）で枠が埋まっていたらバッチごと捨てる。
        // ファイル単体の上限と、画像を含む合計上限の両方を再検証する。
        if (prev.length + files.length > MAX_CHAT_FILE_ATTACHMENTS) return prev;
        if (prev.length + getReservedSlots() + files.length > MAX_TOTAL_CHAT_ATTACHMENTS) return prev;
        return [...prev, ...files.map(toPendingChatFileAttachment)];
      });
    },
    [attachments.length, getReservedSlots, t]
  );

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.id !== id));
  }, []);

  const clear = useCallback(() => {
    setAttachments([]);
  }, []);

  return { attachments, addFiles, removeAttachment, clear };
}
